import type { C1_AxisData, C1_PointData } from "../types/C1_ModelTypes";
import { FURNITURE_WALL_DOCK_DISTANCE } from "./C1_furnitureDocking";
import { projectPointToWall } from "./C1_roomPlacementGeometry";

/** Prefer wall ends, then intersections with guides, wall segments and independent guides. */
export function snapFreeWallEndpoint(
  point: C1_PointData,
  walls: { start: C1_PointData; end: C1_PointData }[],
  disabled = false,
  distance = FURNITURE_WALL_DOCK_DISTANCE,
  guides: { axes?: readonly Pick<C1_AxisData, "kind" | "pos">[]; anchor?: C1_PointData } = {},
): { point: C1_PointData; snapped: boolean } {
  if (disabled) return { point: { ...point }, snapped: false };
  let endpoint: C1_PointData | undefined;
  let nearestEnd = distance + 1e-9;
  for (const wall of walls) for (const candidate of [wall.start, wall.end]) {
    const length = Math.hypot(candidate.x - point.x, candidate.y - point.y);
    if (length < nearestEnd) { endpoint = candidate; nearestEnd = length; }
  }
  if (endpoint) return { point: { ...endpoint }, snapped: true };
  const nearestCoordinate = (coordinate: number, kind: "v" | "h", anchor: number | undefined) => {
    const positions = (guides.axes ?? []).filter(axis => axis.kind === kind).map(axis => axis.pos);
    if (anchor !== undefined) positions.push(anchor);
    let result: number | undefined, nearest = distance + 1e-9;
    for (const position of positions) {
      const delta = Math.abs(position - coordinate);
      if (Number.isFinite(position) && delta < nearest) { result = position; nearest = delta; }
    }
    return result;
  };
  const x = nearestCoordinate(point.x, "v", guides.anchor?.x);
  const y = nearestCoordinate(point.y, "h", guides.anchor?.y);
  let intersection: C1_PointData | undefined;
  let intersectionDistance = distance + 1e-9;
  let nearest: ReturnType<typeof projectPointToWall> | undefined;
  for (const wall of walls) {
    const candidate = projectPointToWall(point, wall);
    if (candidate.distance <= distance && (!nearest || candidate.distance < nearest.distance)) nearest = candidate;
    const dx = wall.end.x - wall.start.x, dy = wall.end.y - wall.start.y;
    const offsets = [
      ...(x !== undefined && Math.abs(dx) > 1e-9 ? [(x - wall.start.x) / dx] : []),
      ...(y !== undefined && Math.abs(dy) > 1e-9 ? [(y - wall.start.y) / dy] : []),
    ];
    for (const offset of offsets) {
      if (offset < 0 || offset > 1) continue;
      const hit = { x: wall.start.x + offset * dx, y: wall.start.y + offset * dy };
      const delta = Math.hypot(hit.x - point.x, hit.y - point.y);
      if (delta < intersectionDistance) { intersection = hit; intersectionDistance = delta; }
    }
  }
  if (intersection) return { point: intersection, snapped: true };
  if (nearest) return { point: nearest.position, snapped: true };
  const guided = { x: x ?? point.x, y: y ?? point.y };
  // Orthogonal alignment must not collapse a short wall onto its opposite endpoint.
  if (guides.anchor && Math.hypot(guided.x - guides.anchor.x, guided.y - guides.anchor.y) < 0.01) {
    if (Math.abs(point.x - guided.x) <= Math.abs(point.y - guided.y)) guided.y = point.y;
    else guided.x = point.x;
  }
  return { point: guided, snapped: x !== undefined || y !== undefined };
}
